import moment from "moment";

/**
 * Lectura del estatus de cancelacion ante el SAT, compartida por las pantallas que
 * lo muestran: Ventas, Cobros de Ventas, Notas de Credito y Devoluciones.
 *
 * Antes cada pantalla tenia su propia copia del `switch` de `estadoCancelacion`
 * —seis copias identicas, dos de ellas en el mismo archivo— y los textos habian
 * divergido ("Sin Informacion" vs "SAT: Sin Informacion", 120px vs 180px de tag).
 */

/**
 * `cancelacion.estadoCancelacion` → texto. Es el estado de la SOLICITUD de
 * cancelacion ante el SAT, no el del comprobante.
 */
export const obtenerEstadoCancelacion = (estadoCancelacion) => {
  switch (estadoCancelacion) {
    case 0:
      return "Sin solicitud";
    case 1:
      return "En proceso";
    case 2:
      return "Plazo vencido";
    case 3:
      return "Solicitud rechazada";
    case 4:
      return "Cancelado sin aceptación";
    case 5:
      return "Cancelado con aceptación";
    default:
      return "En proceso";
  }
};

/** `verificacionSat.esCancelable` → texto. */
export const obtenerEsCancelable = (esCancelable) => {
  switch (esCancelable) {
    case 1:
      return "Cancelable con aceptación";
    case 2:
      return "No cancelable";
    case 3:
      return "Cancelable sin aceptación";
    default:
      return "No disponible";
  }
};

/** "checado hace 3 horas" / "nunca se ha checado". */
export const textoUltimaVerificacion = (fecha) =>
  fecha ? `checado ${moment(fecha).fromNow()}` : "nunca se ha checado";

/**
 * Descriptor del semáforo de estatus SAT de un documento.
 *
 * @param {object} doc Documento con `factura`, `estado`, `cancelacion` y —sólo en
 *                     Ventas— `verificacionSat`.
 * @returns {object|null} `null` si no hay nada que mostrar (documento sin factura).
 *   `{ color, tooltip, texto, tagColor, verificable, discrepancia }`
 *   - `color`     color del icono de la lupa
 *   - `tagColor`  color del `Tag` de antd
 *   - `texto`     etiqueta corta para el tag
 *   - `tooltip`   explicación larga
 *   - `verificable` false = no tiene sentido preguntarle al SAT
 */
export const descriptorEstatusSat = (doc) => {
  if (!doc?.factura) return null;

  const cancelacion = doc.cancelacion || {};
  const verificacion = doc.verificacionSat || {};
  const checado = textoUltimaVerificacion(verificacion.fecha);

  // Cancelada sólo en el sistema (carga histórica): el CFDI sigue vigente en el SAT
  // a propósito, así que no hay estatus fiscal que seguir NI que consultar. Antes la
  // lupa no miraba esta bandera e invitaba a consultar —y a cancelar— un comprobante
  // que se emitió fuera del ERP y debe seguir vivo.
  if (cancelacion.sinSat) {
    return {
      color: "#bfbfbf",
      tagColor: "default",
      texto: "Cancelada solo en el sistema",
      tooltip: "El CFDI no se canceló en el SAT: este documento era una carga histórica y su comprobante sigue vigente a propósito.",
      verificable: false,
      discrepancia: false,
    };
  }

  // ── Cancelada en el ERP: se sigue el avance de la solicitud ────────────────
  if (doc.estado === 0) {
    const detalle = `Estado: ${obtenerEstadoCancelacion(cancelacion.estadoCancelacion)} · ${checado}`;

    if (cancelacion.statusCancelacion === 1) {
      return {
        color: "#fa8c16",
        tagColor: "orange",
        texto: "SAT: Cancelación en proceso",
        tooltip: `El comprobante sigue VIGENTE en el SAT y la solicitud de cancelación está pendiente. ${detalle}`,
        verificable: true,
        discrepancia: false,
      };
    }

    if (cancelacion.statusCancelacion === 2) {
      return {
        color: "#1E88E5",
        tagColor: "blue",
        texto: "SAT: Cancelado",
        tooltip: `El SAT confirma que el comprobante está cancelado. ${detalle}`,
        verificable: true,
        discrepancia: false,
      };
    }

    if (cancelacion.statusCancelacion === 3) {
      return {
        color: "#d4b106",
        tagColor: "yellow",
        texto: "SAT: Rechazada",
        tooltip: `El comprobante sigue VIGENTE en el SAT: la cancelación no prosperó. ${detalle}`,
        verificable: true,
        discrepancia: false,
      };
    }

    return {
      color: "#999999",
      tagColor: "gray",
      texto: "SAT: Sin información",
      tooltip: `Todavía no se sabe cómo quedó la cancelación en el SAT (${checado}).`,
      verificable: true,
      discrepancia: false,
    };
  }

  // ── Viva en el ERP: se verifica que siga vigente en el SAT ─────────────────

  // El SAT la reporta cancelada y aquí sigue viva. Lo detecta el barrido nocturno,
  // que a propósito no aplica la reversa: revertir pólizas, inventario, cobros y
  // banco es decisión de una persona. La lupa es la que la aplica.
  if (verificacion.discrepancia) {
    return {
      color: "#f5222d",
      tagColor: "red",
      texto: "SAT: Cancelada por fuera",
      tooltip: `El SAT reporta este comprobante como CANCELADO, pero en el sistema sigue vigente. Se canceló fuera del ERP (${checado}). Verifica para aplicar la cancelación.`,
      verificable: true,
      discrepancia: true,
    };
  }

  if (verificacion.estado === "No encontrado") {
    return {
      color: "#d4b106",
      tagColor: "yellow",
      texto: "SAT: No encontrado",
      tooltip: `El SAT no encontró este CFDI (${checado}). Revisa que el RFC del emisor, el del receptor y el total coincidan con los del comprobante timbrado.`,
      verificable: true,
      discrepancia: false,
    };
  }

  if (verificacion.estado === "Vigente") {
    return {
      color: "#52c41a",
      tagColor: "green",
      texto: "SAT: Vigente",
      tooltip: `El SAT confirma que el comprobante está vigente (${checado}).`,
      verificable: true,
      discrepancia: false,
    };
  }

  // Nunca verificada: el verde es la creencia del ERP, no una confirmación del SAT.
  // El tooltip lo dice en vez de aparentar que ya se checó.
  return {
    color: "#52c41a",
    tagColor: "green",
    texto: "SAT: Vigente",
    tooltip: "En el sistema está vigente, pero todavía no se ha verificado en el SAT. Da clic para consultarlo.",
    verificable: true,
    discrepancia: false,
  };
};
